import {
  Box,
  ColumnLayout,
  Container,
  Grid,
  Header,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
} from '@cloudscape-design/components';
import { LineItemTypes, PartsTabs } from '@parallel-fluidics/shared/constants';
import { decodePPartId } from '@parallel-fluidics/shared/models/projects/parts';
import PPartDisplay from 'components/Projects/pPartDisplay';
import ProductDisplay from 'features/products/components/ProductDisplay';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useGetOrderById, useGetProjectById } from '../../hooks/use-basicQueries';
import OrgName from '../Display/orgName';
import PackageTable, { CreatePackageButton } from './manageOrder/packageTable';
import AddressDisplay from './package/addressDisplay';

function QuoteIdCell({ projectId }) {
  const { data: project, isFetching } = useGetProjectById(projectId);
  if (isFetching) {
    return 'Loading...';
  }
  if (!project || !project.partId) {
    return 'Not Found';
  }

  // The quote ID is the first 6 characters of the part ID
  return project.partId.substring(0, 6).toUpperCase();
}

QuoteIdCell.propTypes = {
  projectId: PropTypes.string.isRequired,
};

function FilenameCell({ projectId }) {
  const { data: project, isFetching } = useGetProjectById(projectId);
  if (isFetching) {
    return 'Loading...';
  }
  if (!project) {
    return 'Not Found';
  }

  if (project.isInternal) {
    return project.description;
  }

  return (
    <Link href={project.partId ? `/part/${project.partId}` : project.adminLink}>
      {project.filename}
    </Link>
  );
}

FilenameCell.propTypes = {
  projectId: PropTypes.string.isRequired,
};

function MaterialCell({ pPartId }) {
  if (!pPartId) return 'Not Found';
  const { material } = decodePPartId(pPartId);
  if (!material) return 'Not Found';
  return material;
}

MaterialCell.propTypes = {
  pPartId: PropTypes.string,
};

function ProductLineItemContent({ lineItem }) {
  return (
    <Container>
      <SpaceBetween size="xs">
        <Grid
          gridDefinition={[{ colspan: 6 }, { colspan: 3 }, { colspan: 3 }]}
        >
          <div>
            <Box variant="awsui-key-label">Product</Box>
            <div>
              {lineItem.productDocId ? (
                <ProductDisplay
                  productDocId={lineItem.productDocId}
                  fields={['sku', 'name']}
                  link
                />
              ) : (
                lineItem.name || lineItem.sku || '—'
              )}
            </div>
          </div>
          <div>
            <Box variant="awsui-key-label">Version</Box>
            <div>{lineItem.productVersionId || '—'}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">Quantity</Box>
            <div>{lineItem.quantity}</div>
          </div>
        </Grid>
        {!!lineItem.notes && (
          <div>
            <Box variant="awsui-key-label">Notes</Box>
            <div>{lineItem.notes}</div>
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
}

ProductLineItemContent.propTypes = {
  lineItem: PropTypes.shape({
    productDocId: PropTypes.string,
    sku: PropTypes.string,
    name: PropTypes.string,
    productVersionId: PropTypes.string,
    quantity: PropTypes.number,
    notes: PropTypes.string,
  }).isRequired,
};

function LineItemContent({ lineItem, isInternal }) {
  // Branch on line item type — 'product' is catalog/SKU; default is on_demand
  const isProduct = lineItem.type === LineItemTypes.PRODUCT;

  if (isProduct) {
    return <ProductLineItemContent lineItem={lineItem} />;
  }

  return (
    <Container>
      <SpaceBetween size="xs">
        <Grid
          gridDefinition={[
            { colspan: 3 }, { colspan: 3 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 },
          ]}
        >
          <div>
            <Box variant="awsui-key-label">Project ID</Box>
            <div>
              {[lineItem.projectId, lineItem.pPartId].filter(Boolean).join('-')}
              {' '}
              <Link external variant="info" href={`/projects/${lineItem.projectDocId}?partDisplay=${PartsTabs.FLOW}`} />
            </div>
          </div>
          <div>
            <Box variant="awsui-key-label">Kickoff Doc</Box>
            <div>
              {lineItem.kickoffDoc ? (
                <Link href={lineItem.kickoffDoc} external>
                  View Doc
                </Link>
              ) : (
                <Box color="text-status-error">MISSING</Box>
              )}
            </div>
          </div>
          <div>
            <Box variant="awsui-key-label">Quantity</Box>
            <div>{lineItem.quantity}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">Material</Box>
            <div>
              <MaterialCell pPartId={lineItem.pPartId} />
            </div>
          </div>
          <div>
            <Box variant="awsui-key-label">Quote ID</Box>
            <div>
              <QuoteIdCell projectId={lineItem.projectId} />
            </div>
          </div>
        </Grid>
        <Grid
          gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
        >
          <div>
            <Box variant="awsui-key-label">{isInternal ? 'Description' : 'Filename'}</Box>
            <div>
              <FilenameCell projectId={lineItem.projectId} />
            </div>
          </div>
          <div>
            <Box variant="awsui-key-label">Is Capped</Box>
            <div>{lineItem.bonding}</div>
          </div>
        </Grid>
        {/* the frozen customer-facing line from the quote PDF / checkout */}
        {!!lineItem.description && (
          <div>
            <Box variant="awsui-key-label">Quote description</Box>
            <div>{lineItem.description}</div>
          </div>
        )}
        {!!lineItem.notes && (
          <div>
            <Box variant="awsui-key-label">Notes</Box>
            <div>{lineItem.notes || 'N/A'}</div>
          </div>
        )}
        <Table
          columnDefinitions={[
            {
              id: 'type',
              header: 'Type',
              cell: (item) => item.type,
            },
            {
              id: 'part',
              header: 'Part',
              cell: (item) => item.pPartId || 'N/A',
            },
            {
              id: 'goal',
              header: 'Goal',
              cell: (item) => item.goal,
            },
            {
              id: 'name',
              header: 'Name',
              // eslint-disable-next-line react/no-unstable-nested-components
              cell: (item) => (
                <PPartDisplay
                  fieldName="name"
                  projectId={lineItem.projectId}
                  projectDocId={lineItem.projectDocId}
                  pPartId={item.pPartId}
                  pPartDocId={item.pPartDocId}
                  fallbackMessage={item.name || item.type}
                />
              ),
            },
            {
              id: 'note',
              header: 'Note',
              // eslint-disable-next-line react/no-unstable-nested-components
              cell: (item) => (
                <PPartDisplay
                  fieldName="notes"
                  projectId={lineItem.projectId}
                  projectDocId={lineItem.projectDocId}
                  pPartId={item.pPartId}
                  pPartDocId={item.pPartDocId}
                  fallbackMessage={item.note}
                />
              ),
            },
          ]}
          items={lineItem.steps || []}
          variant="embedded"
        />
      </SpaceBetween>
    </Container>
  );
}
LineItemContent.propTypes = {
  lineItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    itemType: PropTypes.string,
    kickoffDoc: PropTypes.string,
    filename: PropTypes.string,
    quoteId: PropTypes.string,
    projectId: PropTypes.string,
    projectDocId: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    bonding: PropTypes.string,
    description: PropTypes.string,
    notes: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string.isRequired,
      goal: PropTypes.number,
      name: PropTypes.string,
      note: PropTypes.string,
    })),
    pPartId: PropTypes.string,
    // product fields
    productDocId: PropTypes.string,
    sku: PropTypes.string,
    name: PropTypes.string,
    productVersionId: PropTypes.string,
  }).isRequired,
  isInternal: PropTypes.bool.isRequired,
};

function OrderDetail({ orderId = null, defaultShipIdx = null, onClose }) {
  const { data: order = {}, isFetching } = useGetOrderById(orderId);

  const [activeTabId, setActiveTabId] = useState(null);

  const isOpen = useMemo(() => !!orderId, [orderId]);

  const tabs = useMemo(() => {
    const shipments = order.shipments || [];
    // show default shipment if exists, otherwise show the first non-shipped shipment
    return shipments.map((shipment, index) => ({
      id: `shipment-${index}`,
      label: `Shipment ${index + 1} (${shipment.targetShipDate})`,
      content: (
        <SpaceBetween size="s">
          <ColumnLayout columns={2}>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">Target Ship Date</Box>
                <div>{shipment.targetShipDate}</div>
              </div>
              {shipment.shippingMethod ? (
                <div>
                  <Box variant="awsui-key-label">Shipping Method</Box>
                  <div>{shipment.shippingMethod}</div>
                </div>
              ) : null}
              {!shipment.isShipped && (
                <CreatePackageButton orderId={orderId} shipmentIdx={index} />
              )}
            </SpaceBetween>
            <div>
              <Box variant="awsui-key-label">Shipping Address</Box>
              <AddressDisplay
                address={shipment.addressData}
                backupAddress={shipment.address}
              />
            </div>
          </ColumnLayout>
          <PackageTable
            isEmbedded
            orderId={orderId}
            shipmentIdx={index}
            trackingData={shipment.trackingData || []}
          />
          {shipment.lineItems.map((item) => (
            <LineItemContent
              key={item.id}
              lineItem={item}
              isInternal={order.isInternal}
            />
          ))}
        </SpaceBetween>
      ),
    }));
  }, [order.isInternal, order.shipments, orderId]);

  useEffect(() => {
    if (defaultShipIdx !== null) {
      // If a default shipment index is provided, set it as the active tab, for order calendar click
      setActiveTabId(`shipment-${defaultShipIdx}`);
      return;
    }

    // If no shipments or only one shipment, do not set active tab
    if (!order?.shipments || order.shipments?.length <= 1) return;

    // Find the first active tab
    const firstNoneShipIdx = order.shipments.findIndex((sh) => !sh.isShipped);
    const firstActiveTabId = firstNoneShipIdx !== -1 ? `shipment-${firstNoneShipIdx}` : 'shipment-0';
    setActiveTabId(firstActiveTabId);
  }, [defaultShipIdx, order?.shipments]);

  if (!orderId) {
    return null;
  }

  return (
    <Modal
      size="large"
      visible={isOpen}
      header={(
        <Header
          variant="h2"
          info={order?.purchaseOrder?.[0]?.downloadURL && <Link href={order.purchaseOrder[0].downloadURL} external variant="info">View PO</Link>}
        >
          {`${order.isInternal ? 'Internal' : 'Customer'} Order Details`}
        </Header>
      )}
      onDismiss={onClose}
    >
      {isFetching ? (
        <StatusIndicator variant="loading">
          Loading...
        </StatusIndicator>
      ) : (
        <SpaceBetween size="s">
          <ColumnLayout columns={2}>
            <div>
              <Box variant="awsui-key-label">Organization</Box>
              {!!order && (
                <div><OrgName orgId={order.organization} /></div>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Order Date</Box>
              <div>{order?.orderDate || 'Loading...'}</div>
            </div>
          </ColumnLayout>
          <hr style={{ margin: 0 }} />
          {/* Only show tabs if there are multiple shipments */}
          {tabs.length > 1 ? (
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
              tabs={tabs}
            />
          ) : tabs[0].content}
        </SpaceBetween>
      )}
    </Modal>
  );
}
OrderDetail.propTypes = {
  orderId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  defaultShipIdx: PropTypes.number,
};

export default OrderDetail;
